import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true, // Send cookies with every request
});

// Also send the localStorage session token as a Bearer header.
// This handles cross-origin deployments (e.g. Vercel → Railway) where
// third-party cookies are blocked by modern browsers.
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('session_token');
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Projects
export const getProjects = async () => {
    const res = await api.get('/api/projects');
    return res.data;
};

export const getProject = async (id) => {
    const res = await api.get(`/api/projects/${id}`);
    return res.data;
};

export const createProject = async (title, file, format = '16:9') => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('format', format);
    if (file) {
        formData.append('file', file);
    }
    const res = await api.post('/api/projects', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
};

export const deleteProject = async (id) => {
    const res = await api.delete(`/api/projects/${id}`);
    return res.data;
};

// Duplicate a project (own or admin-public). Returns the new private project.
export const duplicateProject = async (id) => {
    const res = await api.post(`/api/projects/${id}/duplicate`);
    return res.data;
};

// Admin-only: toggle a project's public flag (templates / demos visible to
// every authenticated user).
export const setProjectBlanking = async (id, ratio) => {
    const res = await api.put(`/api/projects/${id}/blanking`, { ratio: ratio ?? null });
    return res.data;
};

export const setProjectPublic = async (id, isPublic) => {
    const res = await api.put(`/api/projects/${id}/public`, { is_public: !!isPublic });
    return res.data;
};

export const updateShots = async (projectId, shots) => {
    const res = await api.put(`/api/projects/${projectId}/shots`, shots);
    return res.data;
};

export const processProject = async (id, threshold) => {
    const params = threshold !== undefined ? `?threshold=${threshold}` : '';
    const res = await api.post(`/api/projects/${id}/process${params}`);
    return res.data;
};

export const exportProjectPDF = async (id) => {
    const res = await api.get(`/api/projects/${id}/export`);
    return res.data;
};

export const getDownloadPDFUrl = (id) => {
    return `${API_URL}/api/projects/${id}/download`;
};

export const getDownloadZIPUrl = (id) => {
    return `${API_URL}/api/projects/${id}/download-zip`;
};

export const uploadShotImage = async (projectId, shotId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/api/projects/${projectId}/shots/${shotId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
};

// Upload an image picked by the user as a scene asset. The backend converts
// it to neutral grayscale and stores it under /frames/{pid}/layers/, returning
// its URL and intrinsic pixel size so the editor can drop the layer at a
// reasonable default position and scale.
export const uploadSceneAsset = async (projectId, shotId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(
        `/api/projects/${projectId}/shots/${shotId}/scene-asset`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
};

// Layered thumbnail editor — saves the editable scene description plus a
// flattened PNG used by every existing export pipeline. The flat blob is
// produced client-side via Konva.Stage.toBlob so the server stays oblivious
// to the layer model.
export const saveShotScene = async (projectId, shotId, scene, flatBlob) => {
    const formData = new FormData();
    formData.append('scene', JSON.stringify(scene));
    formData.append('flat', flatBlob, `scene_${shotId}.png`);
    const res = await api.put(
        `/api/projects/${projectId}/shots/${shotId}/scene`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
};

// Import a Kinote Edit ZIP and create a new project from it.
export const importProject = async (file, title = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (title.trim()) formData.append('title', title.trim());
    const res = await api.post('/api/projects/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
    });
    return res.data;
};

export const appendVideoToProject = async (projectId, file, sensitivity = 'standard') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sensitivity', sensitivity);
    const res = await api.post(`/api/projects/${projectId}/append-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
};

// Share
export const createShareLink = async (projectId, settings) => {
    const res = await api.post(`/api/projects/${projectId}/share`, settings);
    return res.data;
};

export const getShareLink = async (projectId) => {
    const res = await api.get(`/api/projects/${projectId}/share`);
    return res.data;
};

export const deleteShareLink = async (projectId) => {
    const res = await api.delete(`/api/projects/${projectId}/share`);
    return res.data;
};

// Public share access — pass the share-session access token via Authorization header
// when the link is password-protected.
const _shareHeaders = (accessToken) =>
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

export const getSharedProject = async (token, accessToken) => {
    const res = await api.get(`/api/shared/${token}`, {
        headers: _shareHeaders(accessToken),
    });
    return res.data;
};

export const verifySharePassword = async (token, password) => {
    const res = await api.post(`/api/shared/${token}/verify-password`, { password });
    return res.data;
};

// Comments
export const getSharedComments = async (token, accessToken) => {
    const res = await api.get(`/api/shared/${token}/comments`, {
        headers: _shareHeaders(accessToken),
    });
    return res.data;
};

export const addSharedComment = async (token, shotId, content, accessToken) => {
    const res = await api.post(
        `/api/shared/${token}/shots/${shotId}/comments`,
        { content },
        { headers: _shareHeaders(accessToken) },
    );
    return res.data;
};

export const deleteSharedComment = async (token, commentId, accessToken) => {
    const res = await api.delete(`/api/shared/${token}/comments/${commentId}`, {
        headers: _shareHeaders(accessToken),
    });
    return res.data;
};

export const getProjectComments = async (projectId) => {
    const res = await api.get(`/api/projects/${projectId}/comments`);
    return res.data;
};

export const getSharedDownloadPDFUrl = (token) => {
    return `${API_URL}/api/shared/${token}/download`;
};

export const getSharedDownloadZIPUrl = (token) => {
    return `${API_URL}/api/shared/${token}/download-zip`;
};

export const getDownloadZIPThumbnailsUrl = (id) => {
    return `${API_URL}/api/projects/${id}/download-zip-thumbnails`;
};

export const getDownloadDavinciUrl = (id, frameRate = 24) => {
    return `${API_URL}/api/projects/${id}/export-davinci?frame_rate=${encodeURIComponent(frameRate)}`;
};

export const getSharedDownloadZIPThumbnailsUrl = (token) => {
    return `${API_URL}/api/shared/${token}/download-zip-thumbnails`;
};

export const getSharedDownloadEditUrl = (token) => {
    return `${API_URL}/api/shared/${token}/export-edit`;
};

// Auth
export const getRegistrationStatus = async () => {
    const res = await api.get('/api/auth/registration-status');
    return res.data;
};

export const submitSiteGate = async (password) => {
    const res = await api.post('/api/auth/gate', { password });
    return res.data;
};

// Admin
export const getAdminSettings = async () => {
    const res = await api.get('/api/admin/settings');
    return res.data;
};

export const updateAdminSettings = async (settings) => {
    const res = await api.put('/api/admin/settings', settings);
    return res.data;
};

export const previewAdminPrompt = async (payload) => {
    const res = await api.post('/api/admin/prompt-preview', payload || {});
    return res.data;
};

export const uploadAdminCategoryRefImage = async (kind, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/api/admin/category-reference/${kind}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
};

export const deleteAdminCategoryRefImage = async (kind) => {
    const res = await api.delete(`/api/admin/category-reference/${kind}`);
    return res.data;
};

export const uploadAdminRefImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/api/admin/reference-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
};

export const deleteAdminRefImage = async () => {
    const res = await api.delete('/api/admin/reference-image');
    return res.data;
};

// AI Image Generation
export const generateShotImage = async (projectId, shotId, formData) => {
    const res = await api.post(
        `/api/projects/${projectId}/shots/${shotId}/generate-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return res.data;
};

export const refineShotImage = async (projectId, shotId, formData) => {
    const res = await api.post(
        `/api/projects/${projectId}/shots/${shotId}/refine-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return res.data;
};

export const generateFromSceneImage = async (projectId, shotId, flatBlob, userPrompt, quality) => {
    const formData = new FormData();
    formData.append('flat_image', flatBlob, 'scene_flat.png');
    formData.append('user_prompt', userPrompt || '');
    formData.append('quality', quality || 'fast');
    const res = await api.post(
        `/api/projects/${projectId}/shots/${shotId}/generate-from-scene`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    );
    return res.data;
};

// Visual library (characters / decors / objects)
export const listLibraryItems = async (kind) => {
    const res = await api.get(`/api/library/${kind}`);
    return res.data;
};

export const listAllLibraryItems = async ({ folderId, kind } = {}) => {
    const params = new URLSearchParams();
    if (folderId !== undefined) params.set('folder_id', folderId === null ? 'root' : folderId);
    if (kind) params.set('kind', kind);
    const res = await api.get(`/api/library/items?${params}`);
    return res.data;
};

export const createLibraryItem = async (kind, name, files, folderId, customPrompt = '') => {
    const formData = new FormData();
    formData.append('name', name);
    (files || []).forEach((f) => formData.append('files', f));
    if (folderId) formData.append('folder_id', folderId);
    if (customPrompt?.trim()) formData.append('custom_prompt', customPrompt.trim());
    const res = await api.post(`/api/library/${kind}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
    });
    return res.data;
};

export const refineLibraryItem = async (kind, id, userPrompt = '') => {
    const formData = new FormData();
    formData.append('user_prompt', userPrompt);
    const res = await api.post(`/api/library/${kind}/${id}/refine`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
    });
    return res.data;
};

export const removeLibraryBackground = async (kind, id) => {
    const res = await api.post(`/api/library/${kind}/${id}/remove-background`, {}, {
        timeout: 120000,
    });
    return res.data;
};

// Direct import — converts each file to grayscale server-side and stores it
// as a library item without going through AI synthesis. Returns the array of
// created items.
export const importLibraryItems = async (files, { kind = 'objects', folderId } = {}) => {
    const formData = new FormData();
    (files || []).forEach((f) => formData.append('files', f));
    formData.append('kind', kind);
    if (folderId) formData.append('folder_id', folderId);
    const res = await api.post('/api/library/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    });
    return res.data;
};

export const deleteLibraryItem = async (kind, id) => {
    const res = await api.delete(`/api/library/${kind}/${id}`);
    return res.data;
};

export const renameLibraryItem = async (kind, id, name) => {
    const res = await api.put(`/api/library/${kind}/${id}/rename`, { name });
    return res.data;
};

export const moveLibraryItem = async (kind, id, folderId) => {
    const res = await api.put(`/api/library/${kind}/${id}/move`, { folder_id: folderId || null });
    return res.data;
};

// Library folders
export const listFolders = async () => {
    const res = await api.get('/api/library/folders');
    return res.data;
};

export const createFolder = async (name, parentId) => {
    const res = await api.post('/api/library/folders', { name, parent_id: parentId || null });
    return res.data;
};

export const renameFolder = async (id, name) => {
    const res = await api.put(`/api/library/folders/${id}`, { name });
    return res.data;
};

export const moveFolder = async (id, parentId) => {
    const res = await api.put(`/api/library/folders/${id}/move`, { parent_id: parentId || null });
    return res.data;
};

export const deleteFolder = async (id) => {
    const res = await api.delete(`/api/library/folders/${id}`);
    return res.data;
};

// Duplicate a library folder (own or admin-public). Returns the new private folder.
export const duplicateFolder = async (id) => {
    const res = await api.post(`/api/library/folders/${id}/duplicate`);
    return res.data;
};

// Admin-only: flag a library folder as public so it (and all its descendants)
// are visible to every authenticated user, read-only.
export const setFolderPublic = async (id, isPublic) => {
    const res = await api.put(`/api/library/folders/${id}/public`, { is_public: !!isPublic });
    return res.data;
};

// AI editor persistence
export const updateShotAiConfig = async (projectId, shotId, config) => {
    const res = await api.put(`/api/projects/${projectId}/shots/${shotId}/ai-config`, config || {});
    return res.data;
};

export const uploadShotAiAttachment = async (projectId, shotId, role, file) => {
    const formData = new FormData();
    formData.append('role', role);
    formData.append('file', file);
    const res = await api.post(
        `/api/projects/${projectId}/shots/${shotId}/ai-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
};

// Username self-update
export const updateUsername = async (username) => {
    const res = await api.put('/api/auth/username', { username });
    return res.data;
};

// ==================== Admin supervision ====================

// ── Library Generator (admin) ──────────────────────────────────────────────
export const startLibraryBatch = async (items, quality = 'high') => {
    const res = await api.post('/api/admin/library-generator/start', { items, quality }, { timeout: 30000 });
    return res.data;
};

export const cancelLibraryBatch = async (jobId) => {
    const res = await api.delete(`/api/admin/library-generator/jobs/${jobId}`);
    return res.data;
};

export const getLibraryBatchJob = async (jobId) => {
    const res = await api.get(`/api/admin/library-generator/jobs/${jobId}`);
    return res.data;
};

export const getLibraryBatchJobs = async () => {
    const res = await api.get('/api/admin/library-generator/jobs');
    return res.data;
};

export const getAdminUsers = async () => {
    const res = await api.get('/api/admin/users');
    return res.data;
};

export const adminUpdateUsername = async (userId, username) => {
    const res = await api.put(`/api/admin/users/${userId}/username`, { username });
    return res.data;
};

export const getAdminUserProjects = async (userId) => {
    const res = await api.get(`/api/admin/users/${userId}/projects`);
    return res.data;
};

export const getAdminUserLibrary = async (userId) => {
    const res = await api.get(`/api/admin/users/${userId}/library`);
    return res.data;
};

export default api;
